/**
 * Track-question options (PD-1324b) — shared model.
 *
 * The track questions are Discipline (question 1) then Career track (question 2),
 * and both are offered from the framework's FULL lists. But levels are defined per
 * (discipline, track) PAIRING in `functionTrackLevels`, and a framework need not
 * define every pairing — so a user could pick a combination with no levels at all
 * and walk into an evaluation that can never resolve to a level.
 *
 * Narrowing the track list to the chosen discipline closes that off at the point
 * of choice, which is the only place the user can act on it.
 */

/**
 * The career tracks that actually have at least one level under `functionId`.
 *
 * Fails open, mirroring the ceiling filter in the classification flow: with no
 * discipline chosen yet, or when the discipline covers no track at all, the full
 * list stands rather than leaving the user with nothing to select.
 *
 * @param {Array}  tracks              framework tracks
 * @param {Array}  functionTrackLevels level rows, each { functionId, trackId, ... }
 * @param {*}      functionId          the chosen discipline, or null/undefined
 * @returns {Array} tracks, filtered
 */
export function tracksForFunction(tracks, functionTrackLevels, functionId) {
  const all = tracks || [];
  if (functionId == null) return all;

  const covered = new Set(
    (functionTrackLevels || [])
      .filter((ftl) => ftl.functionId === functionId)
      .map((ftl) => ftl.trackId),
  );

  const available = all.filter((t) => covered.has(t.id));
  return available.length ? available : all;
}

/**
 * True when `trackId` is no longer selectable under `functionId` — i.e. changing
 * the discipline has stranded a track answer that was made under the old one.
 * The caller drops the answer so the user is asked again rather than carrying an
 * invisible, unlevellable pairing forward.
 */
export function trackStrandedByFunction(tracks, functionTrackLevels, functionId, trackId) {
  if (trackId == null || functionId == null) return false;
  return !tracksForFunction(tracks, functionTrackLevels, functionId).some((t) => t.id === trackId);
}

/**
 * The options for one track question, given the answers so far.
 *
 * The two questions are positional by design — the API builds question 1 from the
 * framework's functions and question 2 from its tracks — so the numbering is the
 * contract both portals already key off.
 *
 * @param {Object} question    a track question ({ no, ... })
 * @param {Object} framework   { functions, tracks, functionTrackLevels }
 * @param {Array}  trackAnswers answers recorded so far
 */
export function trackQuestionOptions(question, framework, trackAnswers) {
  if (!question) return [];
  const { functions = [], tracks = [], functionTrackLevels = [] } = framework || {};
  if (question.no === 1) return functions || [];
  if (question.no === 2) {
    const functionId = (trackAnswers || []).find((a) => a.questionNo === 1)?.selectedKey ?? null;
    return tracksForFunction(tracks, functionTrackLevels, functionId);
  }
  return question.answerOptions || [];
}

/**
 * The answers the framework settles on the user's behalf (PD-1324b).
 *
 * A question with exactly one option is not a choice — a single-discipline
 * framework, or a discipline that levels only one career track. Asking it is
 * friction that can only be answered one way, so the answer is recorded and the
 * question is not presented.
 *
 * Walks the questions IN ORDER, accumulating as it goes: settling question 1 can
 * narrow question 2 down to a single track, which then settles too.
 *
 * @returns {Array} the answers to record, in order — empty when nothing settles
 */
export function settledTrackAnswers(trackQuestions, framework, trackAnswers) {
  const sofar = [...(trackAnswers || [])];
  const settled = [];

  for (const question of trackQuestions || []) {
    const options = trackQuestionOptions(question, framework, sofar);
    if (options.length !== 1) continue;

    const i = sofar.findIndex((a) => a.questionNo === question.no);
    // Already on the only option — nothing to record, but it still counts as
    // settled for the questions that follow.
    if (i >= 0 && sofar[i].selectedKey === options[0].id) continue;

    const answer = { questionNo: question.no, selectedKey: options[0].id };
    if (i >= 0) sofar.splice(i, 1, answer);
    else sofar.push(answer);
    settled.push(answer);
  }

  return settled;
}

/**
 * The track questions that still need putting to the user — those with a real
 * choice left in them — each carrying its narrowed `answerOptions`.
 *
 * Callers must record `settledTrackAnswers` first: question 2's option list is
 * derived from question 1's answer, so an unsettled question 1 leaves question 2
 * looking wider than it is.
 */
export function askableTrackQuestions(trackQuestions, framework, trackAnswers) {
  return (trackQuestions || [])
    .map((q) => ({ ...q, answerOptions: trackQuestionOptions(q, framework, trackAnswers) }))
    .filter((q) => q.answerOptions.length > 1);
}
